import { db } from '@/lib/firebase';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, arrayUnion, arrayRemove, query, where, writeBatch, serverTimestamp, DocumentData, DocumentReference, FieldPath } from 'firebase/firestore';

// Types from ControlsManager.util.ts
export interface ControlData {
    id?: string;
    standard?: string;
    section?: string;
    topic?: string;
    controlID?: string;
    controlDescription?: string;
    controlName?: string;
    controlOwner?: string;
    controlApplicable?: boolean;
    status?: string;
    category?: string;
    updatedAt?: any;
    assets?: string[];
    tasks?: string[];
    supportingDocuments?: string[];
    policies?: string[];
    relatedConfigurationStandard?: string[];
    risks?: string[];
    relatedControls?: string[];
    ismsEntries?: string[];
    ismsObjectives?: string[];
    legalObligations?: string[];
    [key: string]: any; // For additional dynamic properties
}

export interface Control extends ControlData { }

export interface ConfigurationStandards {
    [key: string]: {
        controls: ControlData[];
    };
}

export interface ControlsResponse {
    ConfigurationStandards: ConfigurationStandards;
    Controls: ControlData[];
}

// Component link interface for linked items in controls
export interface ComponentLink {
    id: string;              // canonical identifier in the format <scheme>[:<version>]:<ref>
    domain: string;          // classification (Asset, Policy, Risk, Control, etc.)
    scheme: string;          // namespace (internal, iso27001:2013, nist800-53r5, etc.)
    ref: string;             // raw identifier inside the scheme
    title?: string;          // human-readable label (optional)
    relation: string;        // relationship (implements, maps-to, mitigates, etc.)
    confidence: number;      // number between 0-1 for mapping certainty
}

export interface BulkOperation {
    controlIds: string[];
    type: 'assets' | 'documents' | 'policies' | 'risks' | 'controls' | 'legalObligations' | 'linkedComponents';
    action: 'add' | 'remove' | 'update';
    data: any;
    selectedControls?: string[];
    standard?: string;
}

class MCMService {
    private static readonly CONTROLS_COLLECTION = 'compliance';
    private static readonly MAPPING_COLLECTION = 'masterControlMapping';
    private static readonly CUSTOMER_SOA_DOCUMENT = 'customers/{customerID}/product/policyPackages/ISO27001keyDocuments/ismsSoa';
    private static standardNameCache: Record<string, string> = {};

    // Resolve a standard code (e.g., iso27001) to a display name (e.g., ISO 27001) using /modules
    static async resolveStandardName(code: string): Promise<string> {
        if (!code) return 'generic';
        const key = String(code).toLowerCase();
        if (this.standardNameCache[key]) return this.standardNameCache[key];

        try {
            const modulesSnapshot = await getDocs(collection(db, 'modules'));
            for (const moduleDoc of modulesSnapshot.docs) {
                const refName = (moduleDoc.data() as any)?.mcmReference || moduleDoc.id;
                const idLower = moduleDoc.id.toLowerCase();
                const refLower = String(refName).toLowerCase();
                if (idLower === key || refLower === key) {
                    this.standardNameCache[key] = refName;
                    return refName;
                }
            }
        } catch (e) {
            // ignore and fallback to code
        }
        this.standardNameCache[key] = code;
        return code;
    }

    // Get all controls from all modules
    static async getControls(useISM: boolean = false, tenancyID: string): Promise<ControlsResponse> {
        try {
            const controls: ControlData[] = [];
            const configurationStandards: ConfigurationStandards = {};
            
            // Fetch all modules
            const modulesSnapshot = await getDocs(collection(db, 'modules'));
            const customerSOA = await getDoc(doc(db, MCMService.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID)));
            const customerSOAData = customerSOA.data() as {
              [key: string]: any;  
            };
            // Process each module
            for (const moduleDoc of modulesSnapshot.docs) {
                const moduleData = moduleDoc.data() as {
                    displayName?: string;
                    levels?: Array<string>;
                    version?: {
                        controls?: Array<string | { __ref: string | DocumentReference }>;
                        designators?: Array<string>;
                    };
                    [key: string]: any;
                };
                
                const designators = moduleData.version?.designators;
                const levels = moduleData.levels;
                const standardName = moduleDoc.data()?.mcmReference || moduleDoc.id;
                const version = moduleData.version;
                
                if (!version?.controls?.length) continue;
                
                // Initialize standard in configurationStandards if it doesn't exist
                if (!configurationStandards[standardName]) {
                    configurationStandards[standardName] = { controls: [] };
                }
                
                // Process each control reference in the module
                for (const controlRef of version.controls) {
                    try {
                        // Skip empty control references
                        if (!controlRef) continue;
                        
                        let docRef: DocumentReference;
                        
                        // Handle different reference types
                        if (typeof controlRef === 'string') {
                            // Direct string path
                            docRef = doc(db, controlRef);
                        } else if ('__ref' in controlRef) {
                            // Handle { __ref: string | DocumentReference }
                            const ref = controlRef.__ref;
                            if (typeof ref === 'string') {
                                docRef = doc(db, ref);
                            } else if (ref?.path) {
                                docRef = ref;
                            } else {
                                console.warn('Invalid control reference format:', controlRef);
                                continue;
                            }
                        } else if ('path' in controlRef) {
                            // Direct DocumentReference
                            docRef = controlRef;
                        } else {
                            console.warn('Unknown control reference format:', controlRef);
                            continue;
                        }
                        
                        const docSnap = await getDoc(docRef);
                        
                        if (docSnap.exists()) {
                            let data = docSnap.data() as Record<string, any>;
                            if (data.Controls !== undefined) {
                                data = data.Controls;
                            }
                            // data is actually an object of objects - the child objects are our controls so lets loop through instead
                            const standardData = {
                                standard: data.standard || standardName,
                            }
                            for (const key in data) {
                                let controlID = key;
                                let control = data[key];

                                // Create merged control data
                                // Start with the global control as the base
                                let mergedControl = { ...control };
                                
                                // If customer SOA has this control, prioritize its values
                                if (customerSOAData && customerSOAData[controlID]) {
                                    // Merge customer data on top of global data
                                    // This ensures customer values take precedence but we still have global values as fallbacks
                                    mergedControl = { 
                                        ...mergedControl, 
                                        ...customerSOAData[controlID] 
                                    };
                                }

                                const controlData: ControlData = {
                                    refID: docSnap.id,
                                    standard: data.standard || standardName,
                                    // Use merged control data for all fields
                                    section: mergedControl.section || '',
                                    topic: mergedControl.topic || '',
                                    controlID: controlID,
                                    controlName: mergedControl.controlName || controlID,
                                    controlDescription: mergedControl.controlDescription || mergedControl.controlName || '',
                                    controlOwner: mergedControl.controlOwner || '',
                                    controlApplicable: mergedControl.controlApplicable ?? true,
                                    status: mergedControl.status || 'Draft',
                                    category: mergedControl.category || 'Uncategorized',
                                    updatedAt: mergedControl.updatedAt || serverTimestamp(),
                                    // Include all other merged properties
                                    ...mergedControl
                                };
                                
                                if (!standardData[controlID]) {
                                    standardData[controlID] = controlData;
                                }
                            }
                            
                            
                            // Add to controls array if not already present (by ID)
                            if (!controls.some(c => c.standard === standardName)) {
                                controls.push(standardData);
                            }
                            
                            // Add to standard's controls if not already present
                            if (!configurationStandards[standardName].controls.some(
                                (c: ControlData) => c.standard === standardName
                            )) {
                                configurationStandards[standardName].controls.push(standardData);
                            }
                        }
                    } catch (error) {
                        console.warn(`Error processing control reference for ${standardName}:`, error);
                        // Continue with next control reference even if one fails
                    }
                }
            }
            
            return { ConfigurationStandards: configurationStandards, Controls: controls };
        } catch (error) {
            console.error('Error in getControls:', error);
            throw error;
        }
    }

    // Get a single control by ID from customer's MCM; fallback to legacy SOA
    static async getControlById(controlID: string, tenancyID?: string): Promise<ControlData | null> {
        try {
            if (!tenancyID) {
                console.log('[mcm] No tenancy ID provided');
                return null;
            }

            // Test to see if at least a direct hit will work
            // const directControls = await getDocs(collection(db, 'customers', tenancyID, 'mcm', 'ISO 27001', 'controls'));
            // console.log('[mcm] Direct Controls: ', directControls.docs);
            // const directStandards = await getDocs(collection(db, 'customers', tenancyID, 'mcm'));
            // console.log('[mcm] Direct Standards: ', directStandards.docs);

            // Iterate all standards under /customers/{customerID}/mcm
            const standardsCol = collection(db, 'customers', tenancyID, 'mcm');
            // console.log('[mcm] MCM Path: ', 'customers', tenancyID, 'mcm')
            const standardsSnap = await getDocs(standardsCol);
            // console.log('[mcm] Standards Snap: ', standardsSnap)
            if (standardsSnap.empty) {
                // console.log('[mcm] No standards found for tenancy', tenancyID, '— falling back to modules-derived standards');
                try {
                    const modulesSnapshot = await getDocs(collection(db, 'modules'));
                    const candidates = new Set<string>();
                    for (const moduleDoc of modulesSnapshot.docs) {
                        const mData = moduleDoc.data() as any;
                        const standardName = mData?.mcmReference || moduleDoc.id;
                        if (standardName) candidates.add(String(standardName));
                    }
                    for (const std of candidates) {
                        const ctrlRef = doc(db, 'customers', tenancyID, 'mcm', std, 'controls', controlID);
                        const ctrlSnap = await getDoc(ctrlRef);
                        if (ctrlSnap.exists()) {
                            const base = ctrlSnap.data() as any;
                            return {
                                ...base,
                                id: controlID,
                                controlID: base?.controlID || controlID,
                                standard: base?.standard || std,
                            } as ControlData;
                        }
                    }
                } catch (fallbackErr) {
                    console.warn('[mcm] Modules-derived fallback failed:', fallbackErr);
                }
                // continue to legacy SOA fallback below
            }
            for (const stdDoc of standardsSnap.docs) {
                // console.log('[mcm] Checking tenancy', tenancyID);
                // console.log('[mcm] Checking standard', stdDoc.id);
                // console.log('[mcm] Checking control', controlID);
                const ctrlRef = doc(db, 'customers', tenancyID, 'mcm', stdDoc.id, 'controls', controlID);
                const ctrlSnap = await getDoc(ctrlRef);
                if (ctrlSnap.exists()) {
                    const base = ctrlSnap.data() as any;
                    // console.log('base', base);
                    // console.log(`[mcm] ${stdDoc.id} ${controlID}`)
                    const result: ControlData = {
                        ...base,
                        id: controlID,
                        controlID: base?.controlID || controlID,
                        standard: base?.standard || stdDoc.id,
                    };
                    return result;
                }
            }

            // Legacy compatibility: check SOA document for customer-specific control
            try {
                const customerSOA = await getDoc(doc(db, MCMService.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID)));
                const customerSOAData = (customerSOA.data() as { [key: string]: any }) || {};
                if (customerSOAData && customerSOAData[controlID]) {
                    return {
                        ...customerSOAData[controlID],
                        id: controlID,
                    } as ControlData;
                }
            } catch (soaError) {
                console.warn(`Error fetching customer SOA data for control ${controlID}:`, soaError);
            }

            return null;
        } catch (error) {
            console.error('Error fetching control:', error);
            throw error;
        }
    }

    // Helper function to clean up undefined values from an object
    private static cleanUndefinedValues(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }
        
        if (Array.isArray(obj)) {
            return obj.map(item => this.cleanUndefinedValues(item));
        }
        
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value === undefined) {
                return acc;
            }
            return {
                ...acc,
                [key]: this.cleanUndefinedValues(value)
            };
        }, {});
    }

    // Create a new control in the customer's MCM collection
    static async createControl(control: Omit<ControlData, 'controlID'>, tenancyID: string): Promise<ControlData> {
        try {
            // Generate a control ID if not provided
            const controlID = control.controlID || `custom-${Date.now()}`;
            const standard = control.standard || 'generic';
            
            // Prepare the control data and clean undefined values
            const newControl = this.cleanUndefinedValues({
                ...control,
                controlID: controlID,
                id: controlID,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            // Use the MCM reference path pattern: /customers/{customerID}/mcm/{standard}/controls/{controlID}
            const mcmRef = '/customers/{customerID}/mcm/{standard}/controls/{controlID}';
            const controlRef = doc(
                db, 
                mcmRef
                    .replace('{customerID}', tenancyID)
                    .replace('{standard}', standard)
                    .replace('{controlID}', controlID)
            );

            // We need to add a key into the root of the standard collection so it shows up when we loop docs
            const standardRef = doc(db, 'customers', tenancyID, 'mcm', standard);
            await setDoc(standardRef, { 'loopVisibilityKey': true }, { merge: true });

            // Set the control document
            await setDoc(controlRef, newControl);

            return newControl as ControlData;
        } catch (error) {
            // If there's an error, try the legacy SOA document approach as fallback
            if (error.code === 'not-found' || error.code === 'permission-denied') {
                // try {
                //     // const controlID = control.controlID || `custom-${Date.now()}`;
                //     // const newControl = this.cleanUndefinedValues({
                //     //     ...control,
                //     //     controlID: controlID,
                //     //     id: controlID,
                //     //     createdAt: new Date().toISOString(),
                //     //     updatedAt: new Date().toISOString()
                //     // });

                //     // // Fall back to SOA document if MCM path fails
                //     // const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
                //     // await setDoc(customerSOARef, { [controlID]: newControl }, { merge: true });
                //     return newControl as ControlData;
                // } catch (innerError) {
                //     console.error('Error creating SOA document:', innerError);
                //     throw innerError;
                // }
                throw error;
            }
            console.error('Error creating control in SOA:', error);
            throw error;
        }
    }

    // Update an existing control in the customer's SOA document
    static async updateControl(
        controlID: string,
        updates: Partial<ControlData>,
        tenancyID: string
    ): Promise<void> {
        try {
            // Reference to the customer's SOA document
            const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
            
            // Get the current SOA document to check if the control exists
            const customerSOA = await getDoc(customerSOARef);
            const customerSOAData = customerSOA.data() as { [key: string]: any } || {};
            
            // Map generic updates to SOA field names to avoid mismatched keys
            const mappedSOAUpdates: Record<string, any> = {
                updatedAt: new Date().toISOString()
            };
            if (updates.controlName !== undefined) mappedSOAUpdates.controlName = updates.controlName;
            if ((updates as any).controlDesc !== undefined) mappedSOAUpdates.controlDesc = (updates as any).controlDesc; // passthrough if supplied
            if (updates.controlDescription !== undefined) mappedSOAUpdates.controlDesc = updates.controlDescription;
            if (updates.standard !== undefined) mappedSOAUpdates.framework = updates.standard;
            if (updates.controlApplicable !== undefined) mappedSOAUpdates.controlApplicable = updates.controlApplicable;
            if ((updates as any).implementationStatus !== undefined) mappedSOAUpdates.implementationStatus = (updates as any).implementationStatus;
            if (updates.status !== undefined) mappedSOAUpdates.implementationStatus = updates.status as any;
            if (updates.owner !== undefined) mappedSOAUpdates.owner = updates.owner;
            if ((updates as any).controlJustification !== undefined) mappedSOAUpdates.controlJustification = (updates as any).controlJustification;
            
            // If ISM control, write to subcollection /ism/{controlID}; else update SOA root
            if (/^ISM/i.test(controlID)) {
                const ismDocRef = doc(db, 'customers', tenancyID, 'product', 'policyPackages', 'ISO27001keyDocuments', 'ismsSoa', 'ism', controlID);
                const existing = await getDoc(ismDocRef);
                const base = existing.exists() ? existing.data() || {} : {};
                const ismUpdate = MCMService.cleanUndefinedValues({
                    ...base,
                    controlID,
                    controlDescription: (updates as any).controlDescription ?? base.controlDescription ?? updates.controlName,
                    description: (updates as any).controlDescription ?? base.description ?? updates.controlName,
                    implementationStatus: (updates as any).implementationStatus ?? (updates.status as any) ?? base.implementationStatus,
                    implementationDetails: (updates as any).controlJustification ?? base.implementationDetails,
                    owner: updates.owner ?? base.owner,
                    os: updates.controlApplicable === undefined ? base.os : (updates.controlApplicable ? 'yes' : 'no'),
                    updatedAt: new Date().toISOString(),
                });
                await setDoc(ismDocRef, ismUpdate, { merge: true });
            } else {
                // If the control already exists in SOA, merge the updates, else add it
                const mergedNonISM = customerSOAData[controlID]
                    ? { ...customerSOAData[controlID], ...mappedSOAUpdates }
                    : { controlID, ...mappedSOAUpdates };
                if (controlID.includes('.')) {
                    await updateDoc(customerSOARef, new FieldPath(controlID), mergedNonISM);
                } else {
                    await updateDoc(customerSOARef, { [controlID]: mergedNonISM });
                }
            }
            
            // Also update the MCM document for this control
            try {
                // Resolve standard for MCM path
                let std = updates.standard
                    || customerSOAData?.[controlID]?.standard
                    || customerSOAData?.[controlID]?.framework;
                if (!std) {
                    try {
                        const ctrl = await MCMService.getControlById(controlID, tenancyID);
                        std = (ctrl as any)?.standard;
                    } catch (e) {
                        // ignore
                    }
                }
                if (!std) std = 'generic';

                // Map internal code to display name using /modules (e.g., iso27001 -> ISO 27001)
                const stdDisplay = await MCMService.resolveStandardName(String(std));

                // Ensure standard doc exists for listing
                const standardRef = doc(db, 'customers', tenancyID, 'mcm', String(stdDisplay));
                await setDoc(standardRef, { loopVisibilityKey: true }, { merge: true });

                const controlRef = doc(db, 'customers', tenancyID, 'mcm', String(stdDisplay), 'controls', controlID);
                const existing = await getDoc(controlRef);
                const base = existing.exists() ? existing.data() || {} : {};
                const mcmUpdate = MCMService.cleanUndefinedValues({
                    ...base,
                    id: controlID,
                    controlID,
                    standard: stdDisplay,
                    controlName: updates.controlName ?? base?.controlName,
                    controlDescription: updates.controlDescription ?? base?.controlDescription,
                    controlApplicable: updates.controlApplicable ?? base?.controlApplicable,
                    status: updates.status ?? (base as any)?.status,
                    owner: updates.owner ?? (base as any)?.owner,
                    updatedAt: new Date().toISOString(),
                });
                await setDoc(controlRef, mcmUpdate, { merge: true });
            } catch (mcmErr) {
                console.warn('[MCMService.updateControl] Failed to update MCM control doc', mcmErr);
            }

        } catch (error) {
            // If the document doesn't exist yet, create it
            if (error.code === 'not-found') {
                try {
                    if (/^ISM/i.test(controlID)) {
                        const ismDocRef = doc(db, 'customers', tenancyID, 'product', 'policyPackages', 'ISO27001keyDocuments', 'ismsSoa', 'ism', controlID);
                        const ismInitial: any = { updatedAt: new Date().toISOString() };
                        if (updates.controlDescription !== undefined) ismInitial.controlDescription = updates.controlDescription;
                        if (updates.controlName !== undefined) {
                            if (!ismInitial.controlDescription) ismInitial.controlDescription = updates.controlName;
                            ismInitial.description = updates.controlName;
                        }
                        if ((updates as any).implementationStatus !== undefined) ismInitial.implementationStatus = (updates as any).implementationStatus;
                        if (updates.status !== undefined) ismInitial.implementationStatus = updates.status as any;
                        if ((updates as any).controlJustification !== undefined) ismInitial.implementationDetails = (updates as any).controlJustification;
                        if (updates.owner !== undefined) ismInitial.owner = updates.owner;
                        if (updates.controlApplicable !== undefined) ismInitial.os = updates.controlApplicable ? 'yes' : 'no';
                        await setDoc(ismDocRef, { controlID, ...ismInitial }, { merge: true });
                    } else {
                        const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
                        const initialSOA: any = { updatedAt: new Date().toISOString() };
                        if (updates.controlName !== undefined) initialSOA.controlName = updates.controlName;
                        if (updates.controlDescription !== undefined) initialSOA.controlDesc = updates.controlDescription;
                        if (updates.standard !== undefined) initialSOA.framework = updates.standard;
                        if (updates.controlApplicable !== undefined) initialSOA.controlApplicable = updates.controlApplicable;
                        if (updates.status !== undefined) initialSOA.implementationStatus = updates.status as any;
                        if (updates.owner !== undefined) initialSOA.owner = updates.owner;
                        if ((updates as any).controlJustification !== undefined) initialSOA.controlJustification = (updates as any).controlJustification;
                        // Ensure document exists, then set field with dot using FieldPath
                        await setDoc(customerSOARef, { __exists: true }, { merge: true });
                        const value = { controlID, ...initialSOA };
                        if (controlID.includes('.')) {
                            await updateDoc(customerSOARef, new FieldPath(controlID), value);
                        } else {
                            await updateDoc(customerSOARef, { [controlID]: value });
                        }
                    }
                } catch (innerError) {
                    console.error('Error creating SOA document during update:', innerError);
                    throw innerError;
                }
            } else {
                console.error('Error updating control in SOA:', error);
                throw error;
            }
        }
    }

    // Mark a control as deleted in the customer's SOA document
    static async deleteControl(controlID: string, tenancyID: string): Promise<void> {
        try {
            // Reference to the customer's SOA document
            const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
            
            // Get the current SOA document to check if the control exists
            const customerSOA = await getDoc(customerSOARef);
            const customerSOAData = customerSOA.data() as { [key: string]: any } || {};
            
            // If the control exists in SOA, mark it as deleted
            if (customerSOAData[controlID]) {
                await updateDoc(customerSOARef, {
                    [controlID]: {
                        ...customerSOAData[controlID],
                        deleted: true,
                        updatedAt: new Date().toISOString()
                    }
                });
            } else {
                // If the control doesn't exist yet in the SOA, add it with deleted flag
                await updateDoc(customerSOARef, {
                    [controlID]: {
                        controlID,
                        deleted: true,
                        updatedAt: new Date().toISOString()
                    }
                });
            }
            
        } catch (error) {
            // If the document doesn't exist yet, create it with the deleted control
            if (error.code === 'not-found') {
                try {
                    const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
                    await setDoc(customerSOARef, { 
                        [controlID]: {
                            controlID,
                            deleted: true,
                            updatedAt: new Date().toISOString() 
                        }
                    });
                } catch (innerError) {
                    console.error('Error creating SOA document during delete:', innerError);
                    throw innerError;
                }
            } else {
                console.error('Error marking control as deleted in SOA:', error);
                throw error;
            }
        }
    }

    // Bulk update controls in the customer's SOA document
    static async bulkUpdate(operation: BulkOperation, tenancyID: string): Promise<void> {
        try {
            const { controlIds, type, action, data } = operation;
            // console.log('Bulk update operation:', operation);
            const batch = writeBatch(db);
            
            // Reference to the customer's SOA document
            const customerSOARef = doc(db, this.CUSTOMER_SOA_DOCUMENT.replace('{customerID}', tenancyID));
            
            // Get the current SOA document to access existing controls
            const customerSOA = await getDoc(customerSOARef);
            const customerSOAData = customerSOA.data() as { [key: string]: any } || {};

            // Fetch all the control modules to get complete control data
            const modulesSnapshot = await getDocs(collection(db, 'modules'));
            const globalControlsData: { [key: string]: any } = {};
            
            // Process all modules to build a map of all global controls
            for (const moduleDoc of modulesSnapshot.docs) {
                const moduleData = moduleDoc.data() as {
                    version?: {
                        controls?: Array<string | { __ref: string | DocumentReference }>;
                    };
                };
                
                const version = moduleData.version;
                if (!version?.controls?.length) continue;
                
                // Get data from each control document
                for (const controlRef of version.controls) {
                    try {
                        if (!controlRef) continue;
                        
                        let docRef: DocumentReference;
                        
                        if (typeof controlRef === 'string') {
                            docRef = doc(db, controlRef);
                        } else if ('__ref' in controlRef) {
                            const ref = controlRef.__ref;
                            if (typeof ref === 'string') {
                                docRef = doc(db, ref);
                            } else if (ref?.path) {
                                docRef = ref;
                            } else {
                                continue;
                            }
                        } else if ('path' in controlRef) {
                            docRef = controlRef;
                        } else {
                            continue;
                        }
                        
                        const docSnap = await getDoc(docRef);
                        
                        if (docSnap.exists()) {
                            const data = docSnap.data() as Record<string, any>;
                            // Store all controls from this document
                            for (const key in data) {
                                if (key === 'standard') continue; // Skip non-control properties
                                globalControlsData[key] = {
                                    ...data[key],
                                    controlID: key,
                                    refID: docSnap.id,
                                    standard: moduleDoc.data()?.mcmReference || moduleDoc.id || data?.standard || null
                                };
                            }
                        }
                    } catch (err) {
                        console.warn('Error loading control data:', err);
                    }
                }
            }
            
            // Filter out any undefined or null controlIds
            const validControlIds = controlIds.filter(id => id && typeof id === 'string' && id.trim() !== '');
            
            for (const controlId of validControlIds) {
                // First check if control exists in SOA
                let customerControl = customerSOAData[controlId] || null;
                
                // If not in SOA, check global controls
                const globalBase = globalControlsData[controlId] || { controlID: controlId };
                // console.log('globalBase', globalBase)
                // console.log('customerControl', customerControl)
                // Get full control data from global controls if available
                let existingControl = customerControl ? { ...globalBase, ...customerControl } : globalBase;

                // console.log('existingControl', existingControl)
                
                let updatedControl: any;
                
                if (action === 'add') {
                    if (type === 'linkedComponents') {
                        // Handle linkedComponents using the new schema
                        const existingLinks = Array.isArray(existingControl.linkedComponents) ? existingControl.linkedComponents : [];
                        const linksToAdd = Array.isArray(data.links) ? data.links : [];
                        
                        // Create a map of existing links by id to avoid duplicates
                        const existingLinkMap = new Map<string, ComponentLink>(existingLinks.map((link: ComponentLink) => [link.id, link]));
                        
                        // Add new links, overwriting any with the same ID
                        for (const link of linksToAdd) {
                            if (link.id && link.id.trim() !== '') {
                                existingLinkMap.set(link.id, link);
                            }
                        }
                        
                        // Convert back to array
                        const newLinkArray = Array.from(existingLinkMap.values());
                        
                        updatedControl = {
                            ...existingControl,
                            linkedComponents: newLinkArray,
                            updatedAt: new Date().toISOString()
                        };
                    } else {
                        // Legacy handling for other array types
                        const existingArray = Array.isArray(existingControl[type]) ? existingControl[type] : [];
                        
                        // Get items to add - could be data.itemIds (old format) or something else
                        let itemsToAdd: string[] = [];
                        if (data.itemIds) {
                            // Old format - convert to linkedComponents if possible
                            itemsToAdd = data.itemIds.filter((id: string) => id && id.trim() !== '');
                        } else {
                            itemsToAdd = Array.isArray(data) ? data.filter(id => id && id.trim() !== '') : 
                                       (data && typeof data === 'string' && data.trim() !== '' ? [data] : []);
                        }
                        
                        // Add items that don't already exist in the array
                        const newArray = [...new Set([...existingArray, ...itemsToAdd])];
                        
                        updatedControl = {
                            ...existingControl,
                            [type]: newArray,
                            updatedAt: new Date().toISOString()
                        };
                        
                        // If we're adding simple IDs, also convert to linkedComponents format
                        if (itemsToAdd.length > 0) {
                            // Get existing linked components or initialize
                            const existingLinks = Array.isArray(existingControl.linkedComponents) ? 
                                existingControl.linkedComponents : [];
                            
                            // Create map of existing links by id
                            const existingLinkMap = new Map<string, ComponentLink>(existingLinks.map((link: ComponentLink) => [link.id, link]));
                            
                            // Convert simple IDs to ComponentLink objects
                            const newLinks = itemsToAdd.map(id => {
                                // If link already exists, use it
                                const existingLink = existingLinkMap.get(`internal:${id}`);
                                if (existingLink) return existingLink;
                                
                                // Otherwise create a new link with detected domain
                                // Detect domain from ID or infer from type
                                let domain = MCMService.detectDomainFromId(id);
                                
                                // If domain not detected from ID, try to infer from type
                                if (!domain) {
                                    if (type === 'assets') domain = 'Asset';
                                    else if (type === 'policies') domain = 'Policy';
                                    else if (type === 'risks') domain = 'Risk';
                                    else if (type === 'controls') domain = 'Control';
                                    else if (type === 'documents') domain = 'Document';
                                    else domain = 'Other';
                                }
                                
                                // Get appropriate relation for this domain
                                let relation = MCMService.getRelationForDomain(domain);
                                
                                return {
                                    id: `internal:${id}`,
                                    domain,
                                    scheme: 'internal',
                                    ref: id,
                                    relation,
                                    confidence: 0.5
                                } as ComponentLink;
                            });
                            
                            // Add to map (will override any with same ID)
                            for (const link of newLinks) {
                                existingLinkMap.set(link.id, link);
                            }
                            
                            // Update with new linkedComponents array
                            updatedControl.linkedComponents = Array.from(existingLinkMap.values());
                        }
                    }
                } else if (action === 'remove') {
                    if (type === 'linkedComponents') {
                        // Handle removing from linkedComponents using the new schema
                        const existingLinks = Array.isArray(existingControl.linkedComponents) ? 
                            existingControl.linkedComponents : [];
                        const linkIdsToRemove = new Set(
                            Array.isArray(data.linkIds) ? data.linkIds.filter(id => id && id.trim() !== '') : []
                        );
                        
                        // Filter out links with IDs in the removal list
                        const newLinkArray = existingLinks.filter(link => !linkIdsToRemove.has(link.id));
                        
                        updatedControl = {
                            ...existingControl,
                            linkedComponents: newLinkArray,
                            updatedAt: new Date().toISOString()
                        };
                    } else {
                        // Legacy handling for other array types
                        if (Array.isArray(existingControl[type])) {
                            // Get items to remove - could be data.itemIds (old format) or something else
                            let itemsToRemove: string[] = [];
                            if (data.itemIds) {
                                itemsToRemove = data.itemIds.filter((id: string) => id && id.trim() !== '');
                            } else {
                                itemsToRemove = Array.isArray(data) ? data.filter(id => id && id.trim() !== '') : 
                                              (data && typeof data === 'string' && data.trim() !== '' ? [data] : []);
                            }
                            
                            // Remove items
                            const newArray = existingControl[type].filter(
                                (item: any) => !itemsToRemove.includes(item)
                            );
                            
                            updatedControl = {
                                ...existingControl,
                                [type]: newArray,
                                updatedAt: new Date().toISOString()
                            };
                            
                            // If we're removing simple IDs, also update linkedComponents
                            if (itemsToRemove.length > 0 && Array.isArray(existingControl.linkedComponents)) {
                                // Create a set of IDs to remove, including both raw IDs and prefixed IDs
                                const removeIdSet = new Set([
                                    ...itemsToRemove,
                                    ...itemsToRemove.map(id => `internal:${id}`)
                                ]);
                                
                                // Filter out links that match removal criteria
                                const newLinks = existingControl.linkedComponents.filter(link => {
                                    return !removeIdSet.has(link.id) && !removeIdSet.has(link.ref);
                                });
                                
                                updatedControl.linkedComponents = newLinks;
                            }
                        } else {
                            // If the field doesn't exist or isn't an array, just set it to an empty array
                            updatedControl = {
                                ...existingControl,
                                [type]: [],
                                linkedComponents: [],
                                updatedAt: new Date().toISOString()
                            };
                        }
                    }
                } else if (action === 'update') {
                    // Directly update with the provided data
                    updatedControl = {
                        ...existingControl,
                        ...data,
                        updatedAt: new Date().toISOString()
                    };
                }
                
                // Ensure any legacy itemIds field is migrated to linkedComponents if possible
                if (Array.isArray(updatedControl.itemIds) && updatedControl.itemIds.length > 0) {
                    // Get existing or create new linkedComponents array
                    const existingLinks = Array.isArray(updatedControl.linkedComponents) ? 
                        updatedControl.linkedComponents : [];
                    
                    // Create map of existing links by both id and ref (for better matching)
                    const existingLinkMap = new Map<string, ComponentLink>();
                    existingLinks.forEach((link: ComponentLink) => {
                        existingLinkMap.set(link.id, link);
                        existingLinkMap.set(link.ref, link);
                    });
                    
                    // Convert all non-empty itemIds to ComponentLink objects if they don't already exist
                    const itemIdsLinks = updatedControl.itemIds
                        .filter((id: string) => id && id.trim() !== '')
                        .map((id: string) => {
                            // Check if this ID already exists in the link map
                            const existingLink = existingLinkMap.get(`internal:${id}`) || existingLinkMap.get(id);
                            if (existingLink) return existingLink;
                            
                            // Create new link with detected domain
                            const domain = MCMService.detectDomainFromId(id) || 'Other';
                            const relation = MCMService.getRelationForDomain(domain);
                            
                            return {
                                id: `internal:${id}`,
                                domain,
                                scheme: 'internal',
                                ref: id,
                                relation,
                                confidence: 0.7
                            };
                        });
                    
                    // Update linkedComponents with new combined array
                    if (!updatedControl.linkedComponents) {
                        updatedControl.linkedComponents = itemIdsLinks;
                    } else {
                        // Add any new items that don't already exist by ID
                        const newLinksMap = new Map<string, ComponentLink>(updatedControl.linkedComponents.map((link: ComponentLink) => [link.id, link]));
                        itemIdsLinks.forEach(link => {
                            if (!newLinksMap.has(link.id)) {
                                updatedControl.linkedComponents.push(link);
                            }
                        });
                    }
                }
                
                // Only add to batch if controlId is valid and updatedControl has required fields
                if (controlId && typeof controlId === 'string' && updatedControl) {
                    // Ensure the controlID field exists in the updatedControl
                    updatedControl.controlID = controlId;
                    // if (!updatedControl.controlID) {
                    // }
                    
                    const soaSafe: any = {
                        controlID: controlId,
                        controlName: (updatedControl as any).controlName,
                        controlDesc: (updatedControl as any).controlDesc || (updatedControl as any).controlDescription,
                        framework: (updatedControl as any).framework || (updatedControl as any).standard,
                        controlApplicable: (updatedControl as any).controlApplicable,
                        controlJustification: (updatedControl as any).controlJustification,
                        implementationStatus: (updatedControl as any).implementationStatus || (updatedControl as any).status,
                        owner: (updatedControl as any).owner,
                        linkedComponents: Array.isArray((updatedControl as any).linkedComponents) ? (updatedControl as any).linkedComponents : undefined,
                        assets: Array.isArray((updatedControl as any).assets) ? (updatedControl as any).assets : undefined,
                        documents: Array.isArray((updatedControl as any).documents) ? (updatedControl as any).documents : undefined,
                        policies: Array.isArray((updatedControl as any).policies) ? (updatedControl as any).policies : undefined,
                        risks: Array.isArray((updatedControl as any).risks) ? (updatedControl as any).risks : undefined,
                        tasks: Array.isArray((updatedControl as any).tasks) ? (updatedControl as any).tasks : undefined,
                        updatedAt: new Date().toISOString(),
                    };
                    Object.keys(soaSafe).forEach((k) => (soaSafe[k] === undefined ? delete (soaSafe as any)[k] : 0));

                    // If ISM control, write to subcollection /ism/{controlId} as a document; else write to SOA root
                    if (/^ISM/i.test(controlId)) {
                        const ismDocRef = doc(db, 'customers', tenancyID, 'product', 'policyPackages', 'ISO27001keyDocuments', 'ismsSoa', 'ism', controlId);
                        const ismSafe: any = {
                            controlID: controlId,
                            controlDescription: (soaSafe as any).controlDesc || (updatedControl as any).controlDescription || (updatedControl as any).controlName,
                            description: (updatedControl as any).controlDescription || (soaSafe as any).controlDesc || (updatedControl as any).controlName,
                            implementationStatus: (soaSafe as any).implementationStatus,
                            implementationDetails: (soaSafe as any).controlJustification,
                            owner: (soaSafe as any).owner,
                            controlApplicable: typeof (soaSafe as any).controlApplicable === 'boolean' ? ((soaSafe as any).controlApplicable ? 'yes' : 'no') : undefined,
                            updatedAt: (soaSafe as any).updatedAt || new Date().toISOString(),
                        };
                        Object.keys(ismSafe).forEach((k) => (ismSafe[k] === undefined ? delete (ismSafe as any)[k] : 0));
                        batch.set(ismDocRef, ismSafe, { merge: true });
                    } else {
                        if (controlId.includes('.')) {
                            batch.update(
                                customerSOARef,
                                new FieldPath(controlId),
                                soaSafe
                            );
                        } else {
                            batch.set(
                                customerSOARef,
                                { [controlId]: soaSafe },
                                { merge: true }
                            );
                        }
                    }

                    const mcmRef = '/customers/{customerID}/mcm/{standard}/controls/{controlID}';

                    const standardKey = operation.standard || updatedControl.standard || updatedControl.refID || "generic";
                    // Ensure the updatedControl carries the resolved standard
                    if (!updatedControl.standard) {
                        updatedControl.standard = standardKey;
                    }

                    // console.log("MCM Ref: ", mcmRef.replace('{customerID}', tenancyID).replace('{standard}', standardKey).replace('{controlID}', controlId));
                    // console.log("Updated Control: ", updatedControl);
                    // console.log("Standard (resolved): ", standardKey);
                    // console.log("RefID: ", updatedControl.refID);
                    // console.log("ControlID: ", updatedControl.controlID);

                    batch.set(
                        doc(db, mcmRef.replace('{customerID}', tenancyID).replace('{standard}', standardKey).replace('{controlID}', controlId)),
                        updatedControl,
                        { merge: true }
                    );
                } else {
                    console.warn('Skipping invalid control in bulk update:', { controlId, hasUpdatedControl: !!updatedControl });
                }
            }
            
            // Commit all updates at once
            await batch.commit();
        } catch (error) {
            console.error('Error in bulk update to SOA:', error);
            throw error;
        }
    }

    // Search controls
    static async searchControls(qStr: string): Promise<ControlData[]> {
        try {
            const controlsRef = collection(db, this.CONTROLS_COLLECTION);
            const q = query(
                controlsRef,
                where('searchTerms', 'array-contains', qStr.toLowerCase())
            );

            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                ...(doc.data() as any),
                id: doc.id
            })) as ControlData[];
        } catch (error) {
            console.error('Error searching controls:', error);
            throw error;
        }
    }

    // Get controls by standard
    static async getControlsByStandard(standard: string): Promise<ControlData[]> {
        try {
            const controlsRef = collection(db, this.CONTROLS_COLLECTION);
            const q = query(controlsRef, where('standard', '==', standard));

            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            })) as ControlData[];
        } catch (error) {
            console.error(`Error fetching controls for standard ${standard}:`, error);
            throw error;
        }
    }

    // Get controls with pagination
    static async getControlsPaginated(
        page: number = 1,
        limit: number = 50,
        standard?: string
    ): Promise<{ controls: ControlData[]; total: number }> {
        try {
            const baseRef = collection(db, this.CONTROLS_COLLECTION);
            const qRef: any = standard ? query(baseRef, where('standard', '==', standard)) : baseRef;

            const querySnapshot = await getDocs(qRef);
            const allControls = querySnapshot.docs.map(doc => ({
                ...(doc.data() as any),
                id: doc.id
            })) as ControlData[];

            const start = (page - 1) * limit;
            const end = start + limit;
            const paginatedControls = allControls.slice(start, end);

            return {
                controls: paginatedControls,
                total: allControls.length
            };
        } catch (error) {
            console.error('Error fetching paginated controls:', error);
            throw error;
        }
    }
    
    // Detect domain from ID patterns - used for intelligent domain assignment
    static detectDomainFromId(id: string): string | null {
        if (!id) return null;
        
        // Common prefix patterns
        if (/^AST-\d+/i.test(id)) return 'Asset';
        if (/^ASS-\d+/i.test(id)) return 'Assurance';
        // Match any occurrence of 'ass' (case-insensitive) followed by digits, e.g., 'disp_ass001', 'ass001', 'ismass001'
        if (/ass\d+/i.test(id)) return 'Assurance';
        if (/^POL-\d+/i.test(id) || /^policy_\d+/i.test(id)) return 'Policy';
        if (/^RSK-\d+/i.test(id)) return 'Risk';
        if (/^DOC-\d+/i.test(id)) return 'Document';
        if (/^INC-\d+/i.test(id)) return 'Incident';
        if (/^A\.\d+\.\d+/i.test(id) || /^ISO\d+/i.test(id)) return 'Control';
        
        // Try to match common patterns in IDs
        if (id.toLowerCase().includes('asset')) return 'Asset';
        if (id.toLowerCase().includes('policy')) return 'Policy';
        if (id.toLowerCase().includes('risk')) return 'Risk';
        if (id.toLowerCase().includes('doc')) return 'Document';
        if (id.toLowerCase().includes('control')) return 'Control';
        if (id.toLowerCase().includes('incident')) return 'Incident';
        
        // Return null if no match
        return null;
    }
    
    // Get relation type based on domain
    static getRelationForDomain(domain: string): string {
        switch (domain) {
            case 'Asset': return 'impacted-by';
            case 'Document': return 'supports';
            case 'Policy': return 'implements';
            case 'Risk': return 'mitigates';
            case 'Control': return 'maps-to';
            case 'Standard': return 'complies-with';
            case 'Procedure': return 'implements';
            case 'Incident': return 'related-to';
            default: return 'maps-to';
        }
    }

    static normalizeDomainKey(domain: string): string {
        const s = String(domain || '').trim();
        if (!s) return 'Other';
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    static componentDocRef(tenancyID: string, domain: string, componentID: string) {
        const key = this.normalizeDomainKey(domain);
        return doc(db, 'customers', tenancyID, 'mcm', key, 'entities', String(componentID));
    }

    static async getComponentById(domain: string, componentID: string, tenancyID: string): Promise<any | null> {
        if (!tenancyID || !componentID) return null;
        try {
            const ref = this.componentDocRef(tenancyID, domain, componentID);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                const data = snap.data() as any;
                return { id: componentID, domain: this.normalizeDomainKey(domain), ...data };
            }
            return null;
        } catch (e) {
            console.warn('[MCMService.getComponentById] failed', e);
            return null;
        }
    }

    static async updateComponentLinks(tenancyID: string, domain: string, componentID: string, links: ComponentLink[], meta?: { title?: string }): Promise<void> {
        const dKey = this.normalizeDomainKey(domain);
        const stdRef = doc(db, 'customers', tenancyID, 'mcm', dKey);
        await setDoc(stdRef, { loopVisibilityKey: true }, { merge: true });
        const ref = this.componentDocRef(tenancyID, dKey, componentID);
        const payload: any = {
            id: String(componentID),
            domain: dKey,
            linkedComponents: Array.isArray(links) ? links : [],
            updatedAt: new Date().toISOString(),
        };
        if (meta?.title) payload.title = meta.title;
        await setDoc(ref, payload, { merge: true });
    }
}

export default MCMService;
export {
    MCMService,
    MCMService as mcmService
}